import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from '../../../theme';
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import { createPortal } from "react-dom";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import styles from "../SustainQueueScreenStyles";
import { SustainQueueRow, TicketStatus } from "../../../store/slices/ticketingPortalSlice";
import { MergedTicketsBadge } from "./MergedTicketsBadge";

type Group = {
  statusKey: TicketStatus;
  tickets: SustainQueueRow[];
};

type Props = {
  groups: Group[];
  assignableUsers: string[];
  totalCount: number;
  query?: string;
  hasActiveFilters?: boolean;
  isReadOnly?: boolean;
  onTicketPress?: (ticketId: string) => void;
  onMoveTicket?: (ticketId: string, nextStatus: TicketStatus) => void;
  onQuickEdit?: (
    ticketId: string,
    updates: Partial<Pick<SustainQueueRow, "title" | "urgency" | "status" | "assignedTo">>
  ) => void;
  onDuplicateBadgePress?: (ticketId: string) => void;
  /**
   * When set, the kanban board scrolls to the card with this ticket ID after rendering.
   * Used to restore position when returning from Ticket Detail.
   */
  scrollToTicketId?: string | null;
};

const STATUS_LABEL: Record<TicketStatus, string> = {
  new: "New",
  progress: "In Progress",
  "waiting-it": "Escalated to IT",
  "waiting-tmv": "Waiting on Vendor",
  "waiting-vendor": "Waiting on Vendor",
  "submitted-ado": "Submitted to ADO",
  "waiting-user": "Waiting on User",
  "waiting-vendor": "Waiting on Vendor",
  resolved: "Closed",
  "closed-merged": "Merged",
};

const STATUS_DOT_COLOR: Record<TicketStatus, string> = {
  new: "#005CBC",
  progress: "#7871E2",
  "waiting-it": "#FCC42B",
  "waiting-tmv": "#FCC42B",
  "submitted-ado": "#FF8C09",
  "waiting-user": "#FCC42B",
  "waiting-vendor": "#FCC42B",
  resolved: "#6B7280",
  "closed-merged": "#6B7280",
};

const KANBAN_ORDER: TicketStatus[] = [
  "new",
  "progress",
  "waiting-it",
  "waiting-tmv",
  "submitted-ado",
  "waiting-user",
  "resolved",
];

const URGENCY_ORDER: SustainQueueRow["urgency"][] = ["critical", "high", "medium", "low", "informational"];

const URGENCY_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  critical: { label: "Critical", bg: "#FFE6E4", text: "#C23603", dot: "#C23603" },
  high:     { label: "High",     bg: "#FFE9D1", text: "#C56F12", dot: "#D0781D" },
  medium:   { label: "Medium",   bg: "#FFF6CC", text: "#8B6508", dot: "#8B6508" },
  low:      { label: "Low",      bg: "#F1F1F1", text: "#5a5a5a", dot: "#9a9a9a" },
  informational: { label: "Informational", bg: "#AAD2E2", text: "#0078A7", dot: "#0078A7" },
};

// Dark theme: solid dark fills with white text (pale fills + forced-white text are unreadable).
const URGENCY_CONFIG_DARK: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  critical: { label: "Critical", bg: "#9B2626", text: "#FFFFFF", dot: "#FFB09A" },
  high:     { label: "High",     bg: "#8A4B12", text: "#FFFFFF", dot: "#FFC27A" },
  medium:   { label: "Medium",   bg: "#92600A", text: "#FFFFFF", dot: "#FFE08A" },
  low:      { label: "Low",      bg: "#4B4B4B", text: "#FFFFFF", dot: "#D0D0D0" },
  informational: { label: "Informational", bg: "#0D5C73", text: "#FFFFFF", dot: "#7FD8F2" },
};


/**
 * Parse a UTC timestamp from the API that may or may not carry a timezone suffix.
 * Backends often omit the trailing 'Z', causing JS to treat the value as local time
 * (which adds the client's UTC offset — e.g. +05:30 for IST — to every elapsed calc).
 * Always appending 'Z' forces UTC interpretation and gives the correct elapsed duration.
 */
const toUtcMs = (iso: string | null | undefined): number => {
  if (!iso) return 0;
  const normalized = /[Zz]$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const ms = new Date(normalized).getTime();
  return isNaN(ms) ? 0 : ms;
};

const fmtMs = (ms: number): string => {
  if (ms <= 0) return "< 1m";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) { const m = mins % 60; return m > 0 ? `${hours}h ${m}m` : `${hours}h`; }
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  return h > 0 ? `${days}d ${h}h` : `${days}d`;
};


const getColumnDomId = (statusKey: TicketStatus) => `sustain-queue-col-${statusKey}`;
const getCardDomId = (ticketId: string) => `sustain-queue-card-${ticketId}`;
const getEditButtonDomId = (ticketId: string) => `sustain-queue-edit-btn-${ticketId}`;

const SustainQueueKanban: React.FC<Props> = ({
  groups,
  assignableUsers,
  totalCount,
  query,
  hasActiveFilters,
  isReadOnly = false,
  onTicketPress,
  onMoveTicket,
  onQuickEdit,
  onDuplicateBadgePress,
  scrollToTicketId,
}) => {
  const { resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // Live clock — ticks every 30 s to keep timer display in sync
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // ── Scroll-to-card restoration ────────────────────────────────────────────
  // After the kanban renders, scroll the target card into view (web only).
  // Uses the existing getCardDomId naming convention: sustain-queue-card-{id}.
  const totalTicketsCount = useMemo(
    () => groups.reduce((sum, g) => sum + g.tickets.length, 0),
    [groups]
  );
  useEffect(() => {
    if (!scrollToTicketId || totalTicketsCount === 0 || Platform.OS !== "web") return;
    // Use a slightly longer delay to ensure the kanban columns have painted fully.
    // 300ms covers initial layout + React Native Web's deferred DOM rendering.
    const timer = setTimeout(() => {
      try {
        const doc = (globalThis as any).document;
        if (!doc) return;
        const el = doc.getElementById(getCardDomId(scrollToTicketId));
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
          // Briefly highlight the card so the user can spot it easily
          const prevOutline = el.style.outline;
          el.style.outline = "2px solid #005CBC";
          el.style.borderRadius = "8px";
          setTimeout(() => {
            el.style.outline = prevOutline;
          }, 2000);
        }
      } catch {
        // Graceful no-op
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [scrollToTicketId, totalTicketsCount]);

  const [draggingTicketId, setDraggingTicketId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<TicketStatus | null>(null);
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const [hoveredEditTicketId, setHoveredEditTicketId] = useState<string | null>(null);
  const [quickEditTicketId, setQuickEditTicketId] = useState<string | null>(null);
  const [quickEditTitle, setQuickEditTitle] = useState<string>("");
  const [quickEditUrgency, setQuickEditUrgency] = useState<SustainQueueRow["urgency"]>("medium");
  const [quickEditStatus, setQuickEditStatus] = useState<TicketStatus>("new");
  const [quickEditAssignee, setQuickEditAssignee] = useState<string>("Unassigned");
  const [openSelect, setOpenSelect] = useState<"urgency" | "status" | "assignee" | null>(null);
  const [quickEditPosition, setQuickEditPosition] = useState({ top: 120, left: 120 });
  const draggingTicketIdRef = useRef<string | null>(null);

  const ticketMap = useMemo(() => {
    const flatTickets = groups.flatMap((group) => group.tickets);
    return Object.fromEntries(flatTickets.map((ticket) => [ticket.id, ticket]));
  }, [groups]);

  const computePopoverPosition = (anchorRect: { top: number; bottom: number; left: number; right: number }) => {
    const popoverWidth = 340;
    const popoverHeight = 360;
    const viewportPadding = 12;
    const preferredLeft = anchorRect.right - popoverWidth;
    const left = Math.max(
      viewportPadding,
      Math.min(preferredLeft, window.innerWidth - popoverWidth - viewportPadding)
    );
    // Prefer below the anchor; if not enough space, show above
    const spaceBelow = window.innerHeight - anchorRect.bottom - viewportPadding;
    const preferredTop = spaceBelow >= popoverHeight
      ? anchorRect.bottom + 8
      : Math.max(viewportPadding, anchorRect.top - popoverHeight - 8);
    return { top: preferredTop, left };
  };

  const handleCloseQuickEdit = () => {
    setQuickEditTicketId(null);
    setOpenSelect(null);
  };

  const handleOpenQuickEdit = (ticket: SustainQueueRow) => {
    setQuickEditTicketId(ticket.id);
    setQuickEditTitle(ticket.title);
    setQuickEditUrgency(ticket.urgency);
    setQuickEditStatus(ticket.status);
    setQuickEditAssignee(ticket.assignedTo || "Unassigned");
    if (Platform.OS === "web") {
      // nativeID maps to the HTML id attribute in React Native Web.
      // Use it directly — the button is in the DOM at the moment it was clicked.
      const btnNode = document.getElementById(getEditButtonDomId(ticket.id));
      if (btnNode) {
        setQuickEditPosition(computePopoverPosition(btnNode.getBoundingClientRect()));
      } else {
        setQuickEditPosition({ top: 120, left: 20 });
      }
    } else {
      setQuickEditPosition({ top: 120, left: 20 });
    }
    setOpenSelect(null);
  };

  const handleDrop = (statusKey: TicketStatus) => {
    const activeDraggedTicketId = draggingTicketIdRef.current;

    if (!activeDraggedTicketId) {
      return;
    }
    const draggedTicket = ticketMap[activeDraggedTicketId];
    // Access Request tickets are restricted from moving across Kanban columns
    if (!draggedTicket || draggedTicket.status === statusKey || draggedTicket.category === "Access Request") {
      draggingTicketIdRef.current = null;
      setDraggingTicketId(null);
      setDragOverStatus(null);
      return;
    }
    onMoveTicket?.(activeDraggedTicketId, statusKey);
    draggingTicketIdRef.current = null;
    setDraggingTicketId(null);
    setDragOverStatus(null);
  };

  useEffect(() => {
    // Drag-and-drop is disabled entirely for read-only (non-sustain-team) users
    if (Platform.OS !== "web" || isReadOnly) {
      return;
    }

    const cleanupFns: Array<() => void> = [];

    groups.forEach((group) => {
      const columnNode = document.getElementById(getColumnDomId(group.statusKey));

      if (columnNode) {
        const handleDragOver = (event: DragEvent) => {
          event.preventDefault();
          setDragOverStatus(group.statusKey);
        };

        const handleDragLeave = () => {
          setDragOverStatus((currentStatus) =>
            currentStatus === group.statusKey ? null : currentStatus
          );
        };

        const handleColumnDrop = (event: DragEvent) => {
          event.preventDefault();
          handleDrop(group.statusKey);
        };

        columnNode.addEventListener("dragover", handleDragOver as EventListener);
        columnNode.addEventListener("dragenter", handleDragOver as EventListener);
        columnNode.addEventListener("dragleave", handleDragLeave as EventListener);
        columnNode.addEventListener("drop", handleColumnDrop as EventListener);

        cleanupFns.push(() => {
          columnNode.removeEventListener("dragover", handleDragOver as EventListener);
          columnNode.removeEventListener("dragenter", handleDragOver as EventListener);
          columnNode.removeEventListener("dragleave", handleDragLeave as EventListener);
          columnNode.removeEventListener("drop", handleColumnDrop as EventListener);
        });
      }

      group.tickets.forEach((ticket) => {
        const cardNode = document.getElementById(getCardDomId(ticket.id));

        if (!cardNode) {
          return;
        }

        // Access Request tickets are restricted from moving across Kanban columns
        if (ticket.category === "Access Request") {
          cardNode.removeAttribute("draggable");
          return;
        }

        cardNode.setAttribute("draggable", "true");

        const handleDragStart = (event: DragEvent) => {
          draggingTicketIdRef.current = ticket.id;
          setDraggingTicketId(ticket.id);
          event.dataTransfer?.setData("text/plain", ticket.id);
          if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = "move";
          }
        };

        const handleDragEnd = () => {
          draggingTicketIdRef.current = null;
          setDraggingTicketId(null);
          setDragOverStatus(null);
        };

        cardNode.addEventListener("dragstart", handleDragStart as EventListener);
        cardNode.addEventListener("dragend", handleDragEnd as EventListener);

        cleanupFns.push(() => {
          cardNode.removeAttribute("draggable");
          cardNode.removeEventListener("dragstart", handleDragStart as EventListener);
          cardNode.removeEventListener("dragend", handleDragEnd as EventListener);
        });
      });
    });

    return () => {
      cleanupFns.forEach((cleanup) => cleanup());
    };
  }, [groups, ticketMap, onMoveTicket, isReadOnly]);

  useEffect(() => {
    if (Platform.OS !== "web" || !quickEditTicketId) {
      return;
    }

    const reposition = () => {
      const btnNode = document.getElementById(getEditButtonDomId(quickEditTicketId));
      if (btnNode) {
        setQuickEditPosition(computePopoverPosition(btnNode.getBoundingClientRect()));
      }
    };

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const inPopover = target.closest("#sustain-quick-edit-popover");
      const inEditButton = target.closest(`[id^='sustain-queue-edit-btn-']`);

      if (!inPopover && !inEditButton) {
        handleCloseQuickEdit();
      }
    };

    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [quickEditTicketId]);

  const handleSaveQuickEdit = () => {
    if (!quickEditTicketId) {
      return;
    }
    onQuickEdit?.(quickEditTicketId, {
      title: quickEditTitle.trim() || activeQuickEditTicket?.title || "",
      urgency: quickEditUrgency,
      status: quickEditStatus,
      assignedTo: quickEditAssignee,
    });
    handleCloseQuickEdit();
  };

  const activeQuickEditTicket = quickEditTicketId ? ticketMap[quickEditTicketId] : null;
  // Access Request tickets are restricted from changing Kanban status
  const isQuickEditStatusLocked = activeQuickEditTicket?.category === "Access Request";

  // Check if all groups have zero tickets (empty state)
  const totalTicketsInView = groups.reduce((sum, g) => sum + g.tickets.length, 0);
  const showEmptyState = totalTicketsInView === 0;
  const hasActiveSearch = !!(query?.trim());
  const hasActiveSearchOrFilters = hasActiveSearch || hasActiveFilters;

  return (
    <View style={styles.kanbanRoot}>
      {showEmptyState ? (
        // Empty State
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center", paddingVertical: 80 }}>
          {totalCount === 0 && !hasActiveSearchOrFilters ? (
            // No tickets exist at all
            <>
              <MaterialIcons name="inbox" size={48} color="#c0c8d4" style={{ marginBottom: 16 }} />
              <Text style={{ fontSize: 17, fontWeight: "600", color: "#3a3a3a", marginBottom: 8 }}>
                No tickets have been submitted yet.
              </Text>
              <Text style={{ fontSize: 14, color: "#818181", textAlign: "center" }}>
                Tickets will appear here once they are submitted to the Sustain Queue.
              </Text>
            </>
          ) : (
            // Search or filters returned no results
            <>
              <MaterialIcons name="search-off" size={48} color="#c0c8d4" style={{ marginBottom: 16 }} />
              <Text style={{ fontSize: 17, fontWeight: "600", color: "#3a3a3a", marginBottom: 8 }}>
                No tickets match the selected filters
              </Text>
              <Text style={{ fontSize: 14, color: "#818181", textAlign: "center" }}>
                Try adjusting your search or filter criteria.
              </Text>
            </>
          )}
        </View>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.kanbanWrap}>
        {groups.map((group) => (
          <View
            key={group.statusKey}
            nativeID={getColumnDomId(group.statusKey)}
            style={[styles.kanbanCol, dragOverStatus === group.statusKey ? styles.kanbanColDragOver : null]}
          >
          <View style={styles.kanbanColHeader}>
            <View style={styles.kanbanColTitleWrap}>
              <View style={[styles.statusDot, { backgroundColor: STATUS_DOT_COLOR[group.statusKey] }]} />
              <Text style={styles.kanbanColTitle}>{STATUS_LABEL[group.statusKey]}</Text>
            </View>
            <Text style={styles.kanbanColCount}>{group.tickets.length}</Text>
          </View>

          <View
            style={[
              styles.kanbanColBody,
              !group.tickets.length && dragOverStatus === group.statusKey ? styles.kanbanColBodyEmptyDrop : null,
            ]}
          >
            {!group.tickets.length && dragOverStatus === group.statusKey ? (
              <View style={styles.dropPlaceholder}>
                <Text style={styles.dropPlaceholderText}>Drop Ticket Here</Text>
              </View>
            ) : null}

            {group.tickets.map((ticket) => {
              const urgencyConfig = isDarkTheme ? URGENCY_CONFIG_DARK : URGENCY_CONFIG;
              const urg = urgencyConfig[ticket.urgency] ?? urgencyConfig.low;
              const isDragging = draggingTicketId === ticket.id;
              const isCardHovered = hoveredCardId === ticket.id;
              const isEditButtonHovered = hoveredEditTicketId === ticket.id;

              // ── Live timer ─────────────────────────────────────────────
              // Time in Queue = from createdAt (ticket.submitted) to now — toUtcMs
              // normalises strings without a timezone suffix so they're parsed as UTC.
              const liveActiveMs = Math.max(0, now - toUtcMs(ticket.submitted));
              // Waiting on IT = from waitingOnItTimerISO to now (only while in waiting-it status)
              const liveWaitingItMs = (ticket.status === "waiting-it" && ticket.waitingOnItTimerISO)
                ? Math.max(0, now - toUtcMs(ticket.waitingOnItTimerISO))
                : 0;

              // ── Location line (use Access Request locations when present) ──
              let locationLine = ticket.sapId ? `${ticket.location} – ${ticket.sapId}` : ticket.location;
              const arLocs = (ticket as any).accessRequest?.locations as any[] | undefined;
              let levelLabel = ticket.ticketLevel;
              if (Array.isArray(arLocs) && arLocs.length > 0) {
                const first = arLocs[0]?.locationName || arLocs[0]?.locationId || arLocs[0]?.name
                              || arLocs[0]?.region || arLocs[0]?.market || "";
                const extra = arLocs.length - 1;
                locationLine = extra > 0 ? `${first} +${extra}` : first;
                const rawType = (arLocs[0]?.locationType || "").toString().toUpperCase();
                if (rawType === "LOCATION") levelLabel = "Location";
                else if (rawType === "MARKET") levelLabel = "Market";
                else if (rawType === "REGION") levelLabel = "Region";
              }

              // Debug: Log duplicate badge data
              if (ticket.id.includes("00319") || ticket.id.includes("00311") || ticket.hasDuplicate || (ticket.possibleDuplicateCount ?? 0) > 0) {
                console.log(`[SustainQueueKanban] Ticket ${ticket.id} duplicate check:`, {
                  hasDuplicate: ticket.hasDuplicate,
                  possibleDuplicateCount: ticket.possibleDuplicateCount,
                  shouldShowBadge: ticket.hasDuplicate || (ticket.possibleDuplicateCount ?? 0) > 0
                });
              }

              return (
                <Pressable
                  key={ticket.id}
                  nativeID={getCardDomId(ticket.id)}
                  onPress={() => onTicketPress?.(ticket.id)}
                  // @ts-ignore web
                  onMouseEnter={() => setHoveredCardId(ticket.id)}
                  // @ts-ignore web
                  onMouseLeave={() => {
                    setHoveredCardId((id) => (id === ticket.id ? null : id));
                    setHoveredEditTicketId((id) => (id === ticket.id ? null : id));
                  }}
                  style={({ pressed }) => [
                    styles.kanbanCard,
                    pressed ? styles.kanbanCardPressed : null,
                    isDragging ? styles.kanbanCardDragging : null,
                  ]}
                >
                  {/* Duplicate badge removed here; inline badge below remains */}

                  {/* ── Row 1: Ticket ID + (Urgency OR Category/Subcategory for Access Request) ── */}
                  <View style={kc.topRow}>
                    <Text style={kc.ticketId}>{ticket.id}</Text>
                      {ticket.category === "Access Request" ? (
                        (() => {
                          const primaryBg = isDarkTheme ? "#1A5BB5" : "#E6F3FF"; // Access Request pill bg
                          const primaryText = isDarkTheme ? "#FFFFFF" : "#005CBC"; // Access Request text
                          const sub = (ticket.subcategory || "").toLowerCase();
                          let subBg = isDarkTheme ? "#1A5BB5" : "#DDE8FB"; let subText = isDarkTheme ? "#FFFFFF" : "#005CBC";
                          if (sub.includes("temporary")) { subBg = isDarkTheme ? "#4A3FA0" : "#EDEAFB"; subText = isDarkTheme ? "#FFFFFF" : "#6E56D9"; }
                          else if (sub.includes("modify")) { subBg = isDarkTheme ? "#92600A" : "#FFF4D9"; subText = isDarkTheme ? "#FFFFFF" : "#8B6A12"; }
                          else if (sub.includes("new")) { subBg = isDarkTheme ? "#1A5BB5" : "#DDE8FB"; subText = isDarkTheme ? "#FFFFFF" : "#005CBC"; }
                          return (
                            <View style={kc.topRowActions}>
                              <View style={{ flexDirection: "column", alignItems: "flex-end" }}>
                                <View style={[kc.urgencyBadge, { backgroundColor: primaryBg }]}>
                                  <Text style={[kc.urgencyText, { color: primaryText }]} numberOfLines={1}>{ticket.category}</Text>
                                </View>
                                {ticket.subcategory ? (
                                  <View style={[kc.urgencyBadge, { backgroundColor: subBg, marginTop: 6 }]}>
                                    <Text style={[kc.urgencyText, { color: subText }]} numberOfLines={1}>{ticket.subcategory}</Text>
                                  </View>
                                ) : null}
                                {ticket.accessRequestStatus ? (() => {
                                  const arStatusLower = ticket.accessRequestStatus.toLowerCase();
                                  const arStatusBg = arStatusLower.includes("denied")
                                    ? (isDarkTheme ? "#9B2626" : "#FEF2F2")
                                    : arStatusLower.includes("modification")
                                      ? (isDarkTheme ? "#8A4B12" : "#FFF7ED")
                                      : (isDarkTheme ? "#1A6B24" : "#F0FDF4");
                                  const arStatusTextColor = isDarkTheme
                                    ? "#FFFFFF"
                                    : arStatusLower.includes("denied")
                                      ? "#DC2626"
                                      : arStatusLower.includes("modification")
                                        ? "#C2410C"
                                        : "#16A34A";
                                  return (
                                    <View style={[kc.urgencyBadge, { backgroundColor: arStatusBg, marginTop: 6 }]}>
                                      <Text style={[kc.urgencyText, { color: arStatusTextColor }]} numberOfLines={1}>{ticket.accessRequestStatus}</Text>
                                    </View>
                                  );
                                })() : null}
                              </View>
                              {!isReadOnly && ticket.status !== "resolved" && (
                                <Pressable
                                  nativeID={getEditButtonDomId(ticket.id)}
                                  // @ts-ignore web
                                  onMouseEnter={() => setHoveredEditTicketId(ticket.id)}
                                  // @ts-ignore web
                                  onMouseLeave={() => setHoveredEditTicketId((id) => (id === ticket.id ? null : id))}
                                  style={({ pressed }) => [
                                    styles.kcEditBtn,
                                    isCardHovered ? styles.kcEditBtnVisible : null,
                                    isEditButtonHovered ? styles.kcEditBtnHovered : null,
                                    pressed ? styles.kcEditBtnPressed : null,
                                  ]}
                                  onPress={(e) => { (e as any).stopPropagation?.(); handleOpenQuickEdit(ticket); }}
                                >
                                  <MaterialIcons name="edit" size={13} color={isEditButtonHovered ? "#ffffff" : "#585858"} />
                                </Pressable>
                              )}
                            </View>
                          );
                        })()
                      ) : (
                      <View style={kc.topRowActions}>
                      <View style={[kc.urgencyBadge, { backgroundColor: urg.bg }]}>
                        <View style={[kc.urgencyDot, { backgroundColor: urg.dot }]} />
                        <Text style={[kc.urgencyText, { color: urg.text }]}>{urg.label}</Text>
                      </View>

                      {/* Edit button – hidden for read-only (non-sustain-team) users and closed tickets */}
                      {!isReadOnly && ticket.status !== "resolved" && (
                        <Pressable
                          nativeID={getEditButtonDomId(ticket.id)}
                          // @ts-ignore web
                          onMouseEnter={() => setHoveredEditTicketId(ticket.id)}
                          // @ts-ignore web
                          onMouseLeave={() => setHoveredEditTicketId((id) => (id === ticket.id ? null : id))}
                          style={({ pressed }) => [
                            styles.kcEditBtn,
                            isCardHovered ? styles.kcEditBtnVisible : null,
                            isEditButtonHovered ? styles.kcEditBtnHovered : null,
                            pressed ? styles.kcEditBtnPressed : null,
                          ]}
                          onPress={(e) => { (e as any).stopPropagation?.(); handleOpenQuickEdit(ticket); }}
                        >
                          <MaterialIcons name="edit" size={13} color={isEditButtonHovered ? "#ffffff" : "#585858"} />
                        </Pressable>
                      )}

                      {/* Duplicate badge – non-clickable for read-only (non-sustain-team) users */}
                      {(ticket.hasDuplicate || (ticket.possibleDuplicateCount ?? 0) > 0) && (
                        <Pressable
                          onPress={isReadOnly ? undefined : (e) => {
                            (e as any).stopPropagation?.();
                            onDuplicateBadgePress?.(ticket.id);
                          }}
                          style={[kc.dupBadgeBtn, isReadOnly && kc.dupBadgeBtnReadOnly]}
                          hitSlop={4}
                        >
                          <Text style={kc.dupBadgeText}>!</Text>
                        </Pressable>
                      )}

                      {/* Merged tickets badge */}
                      {(ticket.absorbedTicketIds?.length ?? 0) > 0 && (
                        <MergedTicketsBadge
                          ticketsMergedIntoCount={ticket.absorbedTicketIds?.length ?? 0}
                          absorbedTicketIds={ticket.absorbedTicketIds ?? []}
                          mergedTicketsDetails={ticket.mergedTicketsDetails}
                          ticketId={ticket.id}
                          isReadOnly={isReadOnly}
                        />
                      )}
                      </View>
                    )}
                  </View>

                  {/* ── Row 2: Title (bold) ── */}
                  <Text style={kc.title} numberOfLines={2}>{ticket.title}</Text>

                  {/* ── Row 3: Location – SAP ID · Ticket Level ── */}
                  <View style={kc.locationRow}>
                    <MaterialIcons name="location-on" size={11} color="#818181" />
                    <Text style={kc.locationText} numberOfLines={1}>{locationLine}</Text>
                    {levelLabel ? (
                      <Text style={kc.locationLabel}>{levelLabel}</Text>
                    ) : null}
                  </View>

                  {/* ── Row 4: Category + Sub-Category tags (specialized for Access Request) ── */}
                  <View style={kc.tagsRow}>
                    {ticket.category === "Access Request" ? (
                      <>
                          <View style={[kc.tag, kc.tagPrimary]}>
                            <Text style={[kc.tagText, kc.tagPrimaryText]} numberOfLines={1}>
                              {(ticket as any).accessRequest?.accessType || "Access"}
                            </Text>
                          </View>
                          {((ticket as any).accessRequest?.organization || (ticket as any).accessRequest?.department) ? (
                            <View style={[kc.tag, kc.tagOrg]}>
                              <Text style={[kc.tagText, kc.tagOrgText]} numberOfLines={1}>
                                {((ticket as any).accessRequest?.organization || "") + ((ticket as any).accessRequest?.department ? ` • ${(ticket as any).accessRequest.department}` : "")}
                              </Text>
                            </View>
                          ) : null}
                      </>
                    ) : (
                      <>
                        {ticket.category ? (
                          <View style={kc.tag}>
                            <Text style={kc.tagText} numberOfLines={1}>{ticket.category}</Text>
                          </View>
                        ) : null}
                        {ticket.subcategory ? (
                          <View style={[kc.tag, kc.tagSub]}>
                            <Text style={[kc.tagText, kc.tagSubText]} numberOfLines={1}>{ticket.subcategory}</Text>
                          </View>
                        ) : null}
                      </>
                    )}
                  </View>

                  {/* ── Row 5: Time in Queue + Assigned To ── */}
                  <View style={kc.footer}>
                    {/* Assigned To */}
                    <View style={kc.assigneeWrap}>
                      <MaterialIcons name="person-outline" size={12} color={ticket.assignedTo === "Unassigned" ? "#C23603" : "#585858"} />
                      <Text
                        style={ticket.assignedTo === "Unassigned" ? kc.assigneeUnassigned : kc.assignee}
                        numberOfLines={1}
                      >
                        {ticket.assignedTo}
                      </Text>
                    </View>

                    {/* Time in Queue */}
                    <View style={kc.agingWrap}>
                      <Text style={kc.agingPrimary}>{fmtMs(liveActiveMs)}</Text>
                      {liveWaitingItMs > 0 && (
                        <Text style={kc.agingItSecondary}>IT: {fmtMs(liveWaitingItMs)}</Text>
                      )}
                    </View>
                  </View>
                </Pressable>
              );
            })}
          </View>
          </View>
        ))}
      </ScrollView>
      )}

      {quickEditTicketId ? (
        Platform.OS === "web" ? createPortal(
          <>
            <Pressable style={styles.quickEditBackdrop} onPress={handleCloseQuickEdit} />

            <View
              nativeID="sustain-quick-edit-popover"
              style={[
                styles.quickEditPopover,
                { top: quickEditPosition.top, left: quickEditPosition.left } as any,
              ]}
          >
            <View style={styles.quickEditPopoverHeader}>
              <View style={styles.quickEditPopoverTitleWrap}>
                <MaterialIcons name="edit" size={13} color="#2d2d2d" />
                <Text style={styles.quickEditPopoverTitle}>Quick Edit</Text>
              </View>
              <Text style={styles.quickEditPopoverId}>{activeQuickEditTicket?.id}</Text>
            </View>

            <View style={styles.quickEditPopoverBody}>
              <View style={styles.quickEditField}>
                <Text style={styles.quickEditLabel}>Title</Text>
                <TextInput
                  style={styles.quickEditTitleInput}
                  multiline
                  numberOfLines={2}
                  value={quickEditTitle}
                  onChangeText={setQuickEditTitle}
                />
              </View>

              <View style={styles.quickEditFieldRow}>
                {!isQuickEditStatusLocked && (
                <View style={styles.quickEditFieldHalf}>
                  <Text style={styles.quickEditLabel}>Urgency</Text>
                  <Pressable
                    style={styles.quickEditSelect}
                    onPress={() => setOpenSelect((curr) => (curr === "urgency" ? null : "urgency"))}
                  >
                    <Text style={styles.quickEditSelectText}>{URGENCY_CONFIG[quickEditUrgency]?.label ?? quickEditUrgency}</Text>
                    <MaterialIcons name="keyboard-arrow-down" size={16} color="#585858" />
                  </Pressable>
                  {openSelect === "urgency" ? (
                    <View style={styles.quickEditOptions}>
                      <ScrollView style={{ maxHeight: 160 }} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
                      {URGENCY_ORDER.map((urgency) => (
                        <Pressable
                          key={urgency}
                          style={styles.quickEditOption}
                          onPress={() => {
                            setQuickEditUrgency(urgency);
                            setOpenSelect(null);
                          }}
                        >
                          <Text style={styles.quickEditOptionText}>{URGENCY_CONFIG[urgency]?.label ?? urgency}</Text>
                        </Pressable>
                      ))}
                      </ScrollView>
                    </View>
                  ) : null}
                </View>
                )}

                <View style={styles.quickEditFieldHalf}>
                  <Text style={styles.quickEditLabel}>Status</Text>
                  <Pressable
                    style={[styles.quickEditSelect, isQuickEditStatusLocked ? { opacity: 0.6 } : null]}
                    onPress={() => {
                      if (isQuickEditStatusLocked) return;
                      setOpenSelect((curr) => (curr === "status" ? null : "status"));
                    }}
                  >
                    <Text style={styles.quickEditSelectText}>{STATUS_LABEL[quickEditStatus]}</Text>
                    {!isQuickEditStatusLocked && (
                      <MaterialIcons name="keyboard-arrow-down" size={16} color="#585858" />
                    )}
                  </Pressable>
                  {openSelect === "status" && !isQuickEditStatusLocked ? (
                    <View style={styles.quickEditOptions}>
                      <ScrollView style={{ maxHeight: 160 }} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
                      {KANBAN_ORDER.map((status) => (
                        <Pressable
                          key={status}
                          style={styles.quickEditOption}
                          onPress={() => {
                            setQuickEditStatus(status);
                            setOpenSelect(null);
                          }}
                        >
                          <Text style={styles.quickEditOptionText}>{STATUS_LABEL[status]}</Text>
                        </Pressable>
                      ))}
                      </ScrollView>
                    </View>
                  ) : null}
                </View>
              </View>

              <View style={styles.quickEditField}>
                <Text style={styles.quickEditLabel}>Assigned To</Text>
                <Pressable
                  style={styles.quickEditSelect}
                  onPress={() => setOpenSelect((curr) => (curr === "assignee" ? null : "assignee"))}
                >
                  <Text style={styles.quickEditSelectText}>{quickEditAssignee}</Text>
                  <MaterialIcons name="keyboard-arrow-down" size={16} color="#585858" />
                </Pressable>
                {openSelect === "assignee" ? (
                  <View style={styles.quickEditOptions}>
                    <ScrollView style={{ maxHeight: 160 }} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
                    {assignableUsers.filter(a => a !== "Unassigned").map((assignee) => (
                      <Pressable
                        key={assignee}
                        style={styles.quickEditOption}
                        onPress={() => {
                          setQuickEditAssignee(assignee);
                          setOpenSelect(null);
                        }}
                      >
                        <Text style={styles.quickEditOptionText}>{assignee}</Text>
                      </Pressable>
                    ))}
                    </ScrollView>                  </View>
                ) : null}
              </View>
            </View>
            <View style={styles.quickEditFooter}>
              <Pressable style={styles.quickEditCancelBtn} onPress={handleCloseQuickEdit}>
                <Text style={styles.quickEditCancelText}>Cancel</Text>
              </Pressable>
              <Pressable style={styles.quickEditSaveBtn} onPress={handleSaveQuickEdit}>
                <Text style={styles.quickEditSaveText}>Save Changes</Text>
              </Pressable>
            </View>
          </View>
        </>,
        document.body
        ) : (
          <>
            <Pressable style={styles.quickEditBackdrop} onPress={handleCloseQuickEdit} />
            <View
              nativeID="sustain-quick-edit-popover"
              style={[styles.quickEditPopover, styles.quickEditPopoverInline]}
            >
              <View style={styles.quickEditPopoverHeader}>
                <View style={styles.quickEditPopoverTitleWrap}>
                  <MaterialIcons name="edit" size={13} color="#2d2d2d" />
                  <Text style={styles.quickEditPopoverTitle}>Quick Edit</Text>
                </View>
                <Text style={styles.quickEditPopoverId}>{activeQuickEditTicket?.id}</Text>
              </View>
              <View style={styles.quickEditPopoverBody}>
                <View style={styles.quickEditField}>
                  <Text style={styles.quickEditLabel}>Title</Text>
                  <TextInput
                    style={styles.quickEditTitleInput}
                    multiline
                    numberOfLines={2}
                    value={quickEditTitle}
                    onChangeText={setQuickEditTitle}
                  />
                </View>
                <View style={styles.quickEditFieldRow}>
                  {!isQuickEditStatusLocked && (
                  <View style={styles.quickEditFieldHalf}>
                    <Text style={styles.quickEditLabel}>Urgency</Text>
                    <Pressable
                      style={styles.quickEditSelect}
                      onPress={() => setOpenSelect((curr) => (curr === "urgency" ? null : "urgency"))}
                    >
                      <Text style={styles.quickEditSelectText}>{URGENCY_CONFIG[quickEditUrgency]?.label ?? quickEditUrgency}</Text>
                      <MaterialIcons name="keyboard-arrow-down" size={16} color="#585858" />
                    </Pressable>
                  </View>
                  )}
                  <View style={styles.quickEditFieldHalf}>
                    <Text style={styles.quickEditLabel}>Status</Text>
                    <Pressable
                      style={[styles.quickEditSelect, isQuickEditStatusLocked ? { opacity: 0.6 } : null]}
                      onPress={() => {
                        if (isQuickEditStatusLocked) return;
                        setOpenSelect((curr) => (curr === "status" ? null : "status"));
                      }}
                    >
                      <Text style={styles.quickEditSelectText}>{STATUS_LABEL[quickEditStatus]}</Text>
                      {!isQuickEditStatusLocked && (
                        <MaterialIcons name="keyboard-arrow-down" size={16} color="#585858" />
                      )}
                    </Pressable>
                  </View>
                </View>
                <View style={styles.quickEditField}>
                  <Text style={styles.quickEditLabel}>Assigned To</Text>
                  <Pressable
                    style={styles.quickEditSelect}
                    onPress={() => setOpenSelect((curr) => (curr === "assignee" ? null : "assignee"))}
                  >
                    <Text style={styles.quickEditSelectText}>{quickEditAssignee}</Text>
                    <MaterialIcons name="keyboard-arrow-down" size={16} color="#585858" />
                  </Pressable>
                </View>
              </View>
              <View style={styles.quickEditFooter}>
                <Pressable style={styles.quickEditCancelBtn} onPress={handleCloseQuickEdit}>
                  <Text style={styles.quickEditCancelText}>Cancel</Text>
                </Pressable>
                <Pressable style={styles.quickEditSaveBtn} onPress={handleSaveQuickEdit}>
                  <Text style={styles.quickEditSaveText}>Save Changes</Text>
                </Pressable>
              </View>
            </View>
          </>
        )
      ) : null}
    </View>
  );
};

export default SustainQueueKanban;

// ── Card-level styles (kc = kanban card) ─────────────────────────────────────

const kc = StyleSheet.create({
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 5,
  },
  topRowActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  ticketId: {
    fontSize: 11,
    fontWeight: "700",
    color: "#005CBC",
  },
  urgencyBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 100,
  },
  urgencyDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  urgencyText: {
    fontSize: 11,
    fontWeight: "700",
  },
  catBlockWrap: {
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 2,
  },
  catText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#005CBC",
  },
  subCatText: {
    fontSize: 11,
    color: "#4338ca",
  },
  topPill: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  topPillText: {
    fontSize: 11,
    fontWeight: "700",
  },
  locationLabel: {
    fontSize: 11,
    color: "#005CBC",
    marginLeft: 8,
    fontWeight: "700",
  },
  tagPrimary: {
    backgroundColor: "#E6F3FF",
  },
  tagPrimaryText: {
    color: "#005CBC",
  },
  tagOrg: {
    backgroundColor: "#F3F4F6",
  },
  tagOrgText: {
    color: "#3a3a3a",
  },
  title: {
    fontSize: 13,
    fontWeight: "700",
    color: "#1a1a1a",
    lineHeight: 18,
    marginBottom: 7,
  },
  locationRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    marginBottom: 7,
    flexWrap: "nowrap",
  },
  locationText: {
    fontSize: 11,
    color: "#585858",
    flex: 1,
    flexShrink: 1,
  },
  levelPill: {
    backgroundColor: "#EFF6FF",
    borderRadius: 100,
    paddingHorizontal: 6,
    paddingVertical: 1,
    flexShrink: 0,
  },
  levelPillText: {
    fontSize: 10,
    fontWeight: "600",
    color: "#005CBC",
  },
  tagsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginBottom: 8,
  },
  tag: {
    backgroundColor: "#f0f4f8",
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 2,
    maxWidth: 130,
  },
  tagText: {
    fontSize: 10,
    color: "#3a3a3a",
    fontWeight: "600",
  },
  tagSub: {
    backgroundColor: "#eef2ff",
  },
  tagSubText: {
    color: "#4338ca",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    borderTopWidth: 1,
    borderTopColor: "#f0f4f8",
    paddingTop: 6,
    marginTop: 2,
    gap: 8,
  },
  assigneeWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    flex: 1,
    minWidth: 0,
  },
  assignee: {
    fontSize: 11,
    color: "#2d2d2d",
    fontWeight: "600",
    flexShrink: 1,
  },
  assigneeUnassigned: {
    fontSize: 11,
    color: "#C23603",
    fontWeight: "700",
    flexShrink: 1,
  },
  agingWrap: {
    alignItems: "flex-end",
    flexShrink: 0,
  },
  agingPrimary: {
    fontSize: 11,
    fontWeight: "700",
    color: "#3a3a3a",
  },
  agingItSecondary: {
    fontSize: 10,
    color: "#8B6508",
    fontWeight: "500",
    marginTop: 1,
  },
  dupBadgeBtn: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#FBBF24",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    borderWidth: 1,
    borderColor: "#FBBF24",
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  dupBadgeBtnReadOnly: {
    opacity: 0.6,
    ...(Platform.OS === "web" ? { cursor: "default" } as any : {}),
  },
  dupBadgeText: {
    color: "#000000",
    fontWeight: "900",
    fontSize: 12,
    textAlign: "center",
    lineHeight: 14,
  },
});
